import React, { useState, useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import {
  PlusCircleIcon,
  InformationCircleIcon,
  CreditCardIcon,
  BanknotesIcon,
  CurrencyDollarIcon
} from '../utils/icons';

const Accounts = () => {
  const { user } = useUser();
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isLinkInitialized, setIsLinkInitialized] = useState(false);

  // Mock function to simulate fetching connected accounts
  const fetchAccounts = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // In a real app, this would be an API call to your backend
      // const response = await fetch('/api/accounts');
      // const data = await response.json();
      // setAccounts(data.accounts);
      
      // Mock data for demonstration
      setTimeout(() => {
        setAccounts([
          {
            id: '1',
            name: 'Chase Checking',
            type: 'checking',
            last4: '1234',
            balance: 5423.76,
            currency: 'USD',
            status: 'active',
            updatedAt: '2025-06-13T10:30:00Z'
          },
          {
            id: '2',
            name: 'Citi Credit Card',
            type: 'credit card',
            last4: '5678',
            balance: -1250.50,
            currency: 'USD',
            status: 'active',
            updatedAt: '2025-06-13T09:15:00Z'
          }
        ]);
        setIsLoading(false);
      }, 800);
    } catch (err) {
      console.error('Error fetching accounts:', err);
      setError('Failed to load accounts. Please try again later.');
      setIsLoading(false);
    }
  };

  // Initialize Plaid Link
  const initializePlaidLink = () => {
    // In a real app, this would initialize the Plaid Link component
    // For now, we'll simulate a successful connection
    setIsLoading(true);
    setTimeout(() => {
      // Simulate adding a new account
      const newAccount = {
        id: Date.now().toString(),
        name: 'New Bank Account',
        type: 'savings',
        last4: Math.floor(1000 + Math.random() * 9000),
        balance: 0,
        currency: 'USD',
        status: 'active',
        updatedAt: new Date().toISOString()
      };
      
      setAccounts(prev => [newAccount, ...prev]);
      setIsLoading(false);
      setIsLinkInitialized(false);
    }, 2000);
  };

  const handleConnectAccount = () => {
    if (!isLinkInitialized) {
      setIsLinkInitialized(true);
      initializePlaidLink();
    }
  };

  const handleDisconnect = (accountId) => {
    if (window.confirm('Are you sure you want to disconnect this account?')) {
      setAccounts(prev => prev.filter(acc => acc.id !== accountId));
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);  };

  const getAccountIcon = (type) => {
    switch (type) {
      case 'checking':
        return (
          <div className="p-2 rounded-full bg-blue-100 text-blue-600">
            <BanknotesIcon className="h-6 w-6" aria-hidden="true" />
          </div>
        );
      case 'savings':
        return (
          <div className="p-2 rounded-full bg-green-100 text-green-600">
            <CurrencyDollarIcon className="h-6 w-6" aria-hidden="true" />
          </div>
        );
      case 'credit card':
        return (
          <div className="p-2 rounded-full bg-purple-100 text-purple-600">
            <CreditCardIcon className="h-6 w-6" aria-hidden="true" />
          </div>
        );
      default:
        return (
          <div className="p-2 rounded-full bg-gray-100 text-gray-600">
            <InformationCircleIcon className="h-6 w-6" aria-hidden="true" />
          </div>
        );
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Accounts</h1>
          <p className="mt-1 text-gray-600">Manage your connected bank accounts and cards</p>
        </div>
        <button
          onClick={handleConnectAccount}
          disabled={isLoading || isLinkInitialized}
          className={`mt-4 md:mt-0 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${(isLoading || isLinkInitialized) ? 'opacity-70 cursor-not-allowed' : ''}`}
        >
          {isLinkInitialized ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Connecting...
            </>
          ) : (
            'Connect New Account'
          )}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {isLoading && !isLinkInitialized ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : accounts.length > 0 ? (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {accounts.map((account) => (
              <li key={account.id}>
                <div className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      {getAccountIcon(account.type)}
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {account.name} ••••{account.last4}
                        </div>
                        <div className="text-sm text-gray-500 capitalize">
                          {account.type}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`text-lg font-semibold ${account.balance < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                        {formatCurrency(account.balance)}
                      </div>
                      <div className="text-xs text-gray-500">
                        Last updated: {new Date(account.updatedAt).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 flex justify-end space-x-3">
                    <button
                      onClick={() => handleDisconnect(account.id)}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Disconnect
                    </button>
                    <button className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                      View Transactions
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
            />
          </svg>
          <h3 className="mt-2 text-lg font-medium text-gray-900">No accounts connected</h3>
          <p className="mt-1 text-sm text-gray-500">Get started by connecting your first bank account.</p>
          <div className="mt-6">
            <button
              onClick={handleConnectAccount}
              disabled={isLoading || isLinkInitialized}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <PlusCircleIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
              Connect Bank Account
            </button>
          </div>
        </div>
      )}

      <div className="mt-8 bg-blue-50 rounded-lg p-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <InformationCircleIcon className="h-5 w-5 text-blue-400" aria-hidden="true" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
                Your financial data is secure, and access to your accounts is limited to read-only.
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>
                We use bank-level security and encryption to protect your information. Your login credentials are never stored on our servers.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Accounts;
